var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { SelectionType } from '@philips-internal/oswald-ui-constants';
import { AwesomeEvent, ComponentAPI } from '@philips-internal/oswald-ui-core';
import { CollapseEventListener, PageEventListener, RenderEventListener, SelectItemEventListener, SortEventListener, UnselectItemEventListener } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { ExperimentalFlagsService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { scopedWarningMessage } from '@philips-internal/oswald-ui-utils';
import { findRowByKey } from '../table-utils.js';
class TableAPI extends ComponentAPI {
    constructor() {
        super(...arguments);
        /** @event */
        this.onSort = new SortEventListener();
        /** @event */
        this.onDynamicContentRender = new RenderEventListener();
        /** @event */
        this.onNextPage = new PageEventListener();
        /** @event */
        this.onPrevPage = new PageEventListener();
        /** @event */
        this.onCollapse = new CollapseEventListener();
        /** @event */
        this.onSelectItem = new SelectItemEventListener();
        /** @event */
        this.onUnselectItem = new UnselectItemEventListener();
    }
    isValidKey(key) {
        const { data, keyField } = this.state;
        return Boolean(findRowByKey(data, key, keyField));
    }
    checkKeyAndReplaceArray(currentArray, key) {
        return currentArray.includes(key) || !this.isValidKey(key) ?
            [false, []] :
            [true, [key]];
    }
    checkKeyAndAppendToArray(currentArray, key) {
        return currentArray.includes(key) || !this.isValidKey(key) ?
            [false, []] :
            [true, [...currentArray, key]];
    }
    checkKeyAndRemoveFromArray(currentArray, key) {
        return currentArray.includes(key) ?
            [true, [...currentArray].filter((currentKey) => currentKey !== key)] :
            [false, []];
    }
    getData() {
        return this.state.data;
    }
    setData(data) {
        if (Array.isArray(data)) {
            this.setState({ data });
        }
    }
    getEnableColumnCustomization() {
        return this.state.enableColumnCustomization;
    }
    setEnableColumnCustomization(enableColumnCustomization) {
        this.setState({ enableColumnCustomization });
    }
    getKeyField() {
        return this.state.keyField;
    }
    setKeyField(keyField) {
        this.setState({ keyField });
    }
    getSelectionType() {
        return this.state.selectionType;
    }
    setSelectionType(selectionType) {
        this.setState({ selectionType });
    }
    getSelectedRows() {
        const { data, keyField, selectedRows = [] } = this.state;
        return data.filter((row) => selectedRows.includes(String(row[keyField])));
    }
    selectRow(key) {
        const { selectedRows, selectionType } = this.state;
        if (selectionType == SelectionType.NONE) {
            return;
        }
        const [updated, newSelectedRows] = (selectionType == SelectionType.SINGLE) ?
            this.checkKeyAndReplaceArray(selectedRows, key) :
            this.checkKeyAndAppendToArray(selectedRows, key);
        if (updated) {
            this.setState({ selectedRows: newSelectedRows });
        }
    }
    unselectRow(key) {
        const { selectedRows } = this.state;
        const [updated, newSelectedRows] = this.checkKeyAndRemoveFromArray(selectedRows, key);
        if (updated) {
            this.setState({ selectedRows: newSelectedRows });
        }
    }
    selectAll() {
        const { data, keyField, selectionType } = this.state;
        if (selectionType != SelectionType.MULTIPLE) {
            return;
        }
        this.setState({ selectedRows: data.map((row) => String(row[keyField])) });
    }
    unselectAll() {
        this.setState({ selectedRows: [] });
    }
    getHiddenColumns() {
        return this.state.hiddenColumns;
    }
    setHideColumns(column) {
        const empty = [];
        const columns = empty.concat(column);
        const hiddenColumns = [...this.state.hiddenColumns, ...columns];
        this.setState({ hiddenColumns });
    }
    showColumns(column) {
        const empty = [];
        const columns = empty.concat(column);
        const hiddenColumns = this.getHiddenColumns();
        this.setState({
            hiddenColumns: hiddenColumns.filter((column) => !columns.includes(column)),
        });
    }
    // Just to comply with the collapsible interface.
    isCollapsed() {
        return false;
    }
    // @ts-ignore: Just to comply with the Collapsible interface
    setCollapsed(value) {
        return;
    }
    getExpandedRows() {
        const { data, keyField, expandedRows = [] } = this.state;
        return data.filter((row) => expandedRows.includes(String(row[keyField])));
    }
    expandRow(key) {
        const { expandedRows } = this.state;
        const [updated, newExpandedRows] = this.checkKeyAndAppendToArray(expandedRows, key);
        if (updated) {
            this.setState({ expandedRows: newExpandedRows });
        }
    }
    collapseRow(key) {
        const { expandedRows } = this.state;
        const [updated, newExpandedRows] = this.checkKeyAndRemoveFromArray(expandedRows, key);
        if (updated) {
            this.setState({ expandedRows: newExpandedRows });
        }
    }
    collapseAll() {
        this.setState({ expandedRows: [] });
    }
    expandAll() {
        const { data, keyField } = this.state;
        this.setState({ expandedRows: data.map((row) => String(row[keyField])) });
    }
    isTree() {
        if (!ExperimentalFlagsService.getEnableTreeTable()) {
            scopedWarningMessage('table', 'Property `isTree` will only have an effect if the experimental flag `ENABLE_TREE_TABLE` is enabled');
        }
        return Boolean(this.state.isTree);
    }
    setIsTree(isTree) {
        if (!ExperimentalFlagsService.getEnableTreeTable()) {
            scopedWarningMessage('table', 'Property `isTree` will only have an effect if the experimental flag `ENABLE_TREE_TABLE` is enabled');
        }
        this.setState({ isTree });
    }
    getTreeGroupLabelKey() {
        if (!ExperimentalFlagsService.getEnableTreeTable()) {
            scopedWarningMessage('table', 'Property `treeGroupLabelKey` will only have an effect if the experimental flag `ENABLE_TREE_TABLE` is enabled');
        }
        return this.state.treeGroupLabelKey;
    }
    setTreeGroupLabelKey(treeGroupLabelKey) {
        if (!ExperimentalFlagsService.getEnableTreeTable()) {
            scopedWarningMessage('table', 'Property `treeGroupLabelKey` will only have an effect if the experimental flag `ENABLE_TREE_TABLE` is enabled');
        }
        this.setState({ treeGroupLabelKey });
    }
}
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], TableAPI.prototype, "onSort", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], TableAPI.prototype, "onDynamicContentRender", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], TableAPI.prototype, "onNextPage", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], TableAPI.prototype, "onPrevPage", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], TableAPI.prototype, "onCollapse", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], TableAPI.prototype, "onSelectItem", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], TableAPI.prototype, "onUnselectItem", void 0);
export default TableAPI;
//# sourceMappingURL=table-api.js.map